import { useMutation, useQuery } from "@apollo/client";
import ReportProblem from "@mui/icons-material/ReportProblem";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Save from "@mui/icons-material/Save";
import SignalCellularAlt from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2Bar from "@mui/icons-material/SignalCellularAlt2Bar";
import SignalCellularAlt1Bar from "@mui/icons-material/SignalCellularAlt1Bar";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LocalOffer from "@mui/icons-material/LocalOffer";
import Style from "@mui/icons-material/Style";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import {
  GridSortModel,
  GridRowParams,
  GridRowId,
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
  gridDetailPanelExpandedRowIdsSelector,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-premium";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { LinearIcon } from "components/LinearIcon";
import { LinearTagChip } from "components/LinearTagChip";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { useLinearTags } from "hooks/useLinearTags";
import {
  ERROR_CLUSTER_RUN_ID_KEY,
  ERROR_CLUSTER_GROUP_ID_KEY,
} from "encamp-omnisearch/src/constants/keyNames";
import { ErrorGroupDetailPanel } from "./ErrorGroupDetailPanel";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  ErrorClusterGroupsByRunIdQuery,
  ErrorClusterGroupsByRunIdQueryVariables,
  ErrorsBlockingCohortQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTimeMed } from "util/dates";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";
import { safeParseFloat } from "components/Payment/lib/numberUtils";

const QUEUE_ERROR_CLUSTERING_JOB_MUTATION = gql(`
  mutation QueueErrorClusteringJob($input: QueueErrorClusteringJobInput!) {
    queueErrorClusteringJob(input: $input) {
      id
      status
    }
  }
`);

const ERROR_CLUSTERING_CONFIG_QUERY = gql(`
  query ErrorClusteringConfig {
    errorClusteringConfig {
      distanceThreshold
    }
  }
`);

const UPSERT_ERROR_CLUSTERING_DISTANCE_THRESHOLD_MUTATION = gql(`
  mutation UpsertErrorClusteringDistanceThreshold($value: Float!) {
    upsertErrorClusteringDistanceThreshold(value: $value)
  }
`);

const ERROR_CLUSTER_RUNS_QUERY = gql(`
  query ErrorClusterRuns($page: Int, $pageSize: Int) {
    errorClusterRuns(page: $page, pageSize: $pageSize) {
      count
      items {
        runId
        updatedAt
        groupCount
        totalErrors
        distanceThreshold
      }
    }
  }
`);

export const ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY = gql(`
  query ErrorClusterGroupsByRunId($runId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    errorClusterGroupsByRunId(runId: $runId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        groupId
        errorPattern
        groupSize
        facilitiesInGroupCount
        facilityIds
        workflowIds
        workflowTags
        untaggedWorkflowCount
        workflowsWithTags {
          workflowId
          tags
        }
        states
      }
    }
  }
`);

const ERRORS_BLOCKING_COHORT_QUERY = gql(`
  query ErrorsBlockingCohort($runId: String!) {
    errorsBlockingCohort(runId: $runId) {
      count
      items {
        workflowId
        cohortId
        groupId
        activityType
        cohortMembers
        errorMessage
      }
    }
  }
`);

const CREATE_LINEAR_ISSUE_MUTATION = gql(`
  mutation CreateLinearIssue($input: CreateLinearIssueInput!) {
    createLinearIssue(input: $input) {
      success
      issueIdentifier
      issueUrl
      issueId
    }
  }
`);

export type ErrorClusterGroupRow = NonNullable<
  NonNullable<
    ErrorClusterGroupsByRunIdQuery["errorClusterGroupsByRunId"]
  >["items"]
>[number];

export type ErrorBlockingCohortRow = NonNullable<
  NonNullable<ErrorsBlockingCohortQuery["errorsBlockingCohort"]>["items"]
>[number];

// Expand/collapse cell component
function ExpandCollapseCell({
  row,
  id,
}: {
  row: ErrorClusterGroupRow;
  id: GridRowId;
}): JSX.Element | null {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const isExpanded = expandedRowIds.has(id);
  const hasWorkflows = row.workflowIds && row.workflowIds.length > 0;

  if (!hasWorkflows) {
    return null;
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        apiRef.current.toggleDetailPanel(id);
      }}
    >
      {isExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
}

export function ErrorClustering() {
  const alerts = useAlerts();

  useBreadcrumb({
    label: "Error Triage",
    to: "/staff/fulfillment/error-clustering",
  });

  // Query for current error clustering config (server provides the default)
  const { data: configData, loading: configLoading } = useQuery(
    ERROR_CLUSTERING_CONFIG_QUERY
  );
  const savedDistanceThreshold =
    configData?.errorClusteringConfig?.distanceThreshold;

  // State for distance threshold input (controlled input)
  const [distanceThresholdInput, setDistanceThresholdInput] = useState<
    string | null
  >(null);

  // Update input when config loads (server provides the default)
  useEffect(() => {
    if (savedDistanceThreshold != null) {
      setDistanceThresholdInput(savedDistanceThreshold.toString());
    }
  }, [savedDistanceThreshold]);

  // Mutation for saving the distance threshold config
  const [upsertDistanceThreshold, { loading: savingConfig }] = useMutation(
    UPSERT_ERROR_CLUSTERING_DISTANCE_THRESHOLD_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTERING_CONFIG_QUERY],
      onCompleted: () => {
        alerts.success("Distance threshold saved successfully!");
      },
      onError: (error) => {
        alerts.error("Failed to save distance threshold", error);
      },
    }
  );

  const handleSaveDistanceThreshold = useCallback(() => {
    if (distanceThresholdInput === null) {
      alerts.error("Distance threshold value is required");
      return;
    }
    const value = safeParseFloat(distanceThresholdInput);
    if (value === null || value < 0.5 || value > 2.0) {
      alerts.error("Distance threshold must be between 0.5 and 2.0");
      return;
    }
    upsertDistanceThreshold({ variables: { value } });
  }, [distanceThresholdInput, upsertDistanceThreshold, alerts]);

  // Check if there are unsaved changes
  const parsedInput =
    distanceThresholdInput !== null
      ? safeParseFloat(distanceThresholdInput)
      : null;
  const hasUnsavedChanges =
    savedDistanceThreshold != null &&
    parsedInput !== null &&
    parsedInput !== savedDistanceThreshold;

  // Mutation for queuing error clustering job
  const [queueJob, { loading: queueJobLoading }] = useMutation(
    QUEUE_ERROR_CLUSTERING_JOB_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_RUNS_QUERY],
      onCompleted: () => {
        alerts.success("Error clustering job queued successfully!");
      },
      onError: (error) => {
        alerts.error("Failed to queue job", error);
      },
    }
  );

  const handleQueueJob = useCallback(() => {
    // Use the current input value for the job (not necessarily saved)
    // Server always provides a default via config query, so we always have a value
    if (distanceThresholdInput === null) {
      alerts.error("Distance threshold value is required");
      return;
    }
    const distanceThreshold = safeParseFloat(distanceThresholdInput);
    if (distanceThreshold === null) {
      alerts.error("Invalid distance threshold value");
      return;
    }
    queueJob({
      variables: {
        input: {
          distanceThreshold,
        },
      },
    });
  }, [queueJob, distanceThresholdInput, alerts]);

  // Fetch available runs
  const { data: runsData, loading: runsLoading } = useQuery(
    ERROR_CLUSTER_RUNS_QUERY,
    {
      variables: {
        page: 0,
        pageSize: 100, // Get first 100 runs for the dropdown
      },
    }
  );

  // Default to the latest run (first in the list since ordered by updatedAt DESC)
  const latestRunId = runsData?.errorClusterRuns?.items?.[0]?.runId;
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(
    latestRunId
  );

  // Update selected run when data loads
  useEffect(() => {
    if (latestRunId && !selectedRunId) {
      setSelectedRunId(latestRunId);
    }
  }, [latestRunId, selectedRunId]);

  // State for manage tags dialog
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [selectedGroupForTags, setSelectedGroupForTags] =
    useState<ErrorClusterGroupRow | null>(null);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);

  // Mutation for updating workflow tags
  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY],
    }
  );

  // Mutation for creating Linear issues
  const [createLinearIssue, { loading: createLinearLoading }] = useMutation(
    CREATE_LINEAR_ISSUE_MUTATION
  );

  // Track which group is currently creating a Linear ticket (for loading state)
  const [creatingTicketForGroupId, setCreatingTicketForGroupId] = useState<
    number | null
  >(null);

  // State for Linear ticket confirmation dialog
  const [linearConfirmDialogOpen, setLinearConfirmDialogOpen] = useState(false);
  const [selectedGroupForLinear, setSelectedGroupForLinear] =
    useState<ErrorClusterGroupRow | null>(null);
  const [linearTicketTitle, setLinearTicketTitle] = useState("");
  const [linearTicketStatus, setLinearTicketStatus] = useState("Triage");
  const [linearTicketPriority, setLinearTicketPriority] = useState(0);

  // State for untagged filter toggle
  const [currentSearch, setCurrentSearch] = useState("");
  const omnisearchInputRef = useRef<HTMLInputElement>(null);

  // API ref for detail panel control
  const apiRef = useGridApiRef();

  // Fetch error cluster groups data to collect all tags for Linear integration
  // Use a large pageSize to ensure we get all tags for Linear status lookup
  const { data: errorClusterGroupsData } = useQuery<
    ErrorClusterGroupsByRunIdQuery,
    ErrorClusterGroupsByRunIdQueryVariables
  >(ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY, {
    variables: { 
      runId: selectedRunId ?? "",
      page: 0,
      pageSize: 10000, // Large page size to get all tags
      // Omit search and sort instead of passing undefined
    },
    skip: !selectedRunId,
    fetchPolicy: "cache-and-network",
  });

  // Collect all tags from all error cluster groups for Linear issue lookup
  const allTagsForLinear = useMemo(() => {
    const tagSet = new Set<string>();
    const items = errorClusterGroupsData?.errorClusterGroupsByRunId?.items ?? [];
    items.forEach((row) => {
      row.workflowTags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet);
  }, [errorClusterGroupsData]);

  // Get Linear issue statuses for all tags
  const { statusMap: linearStatusMap } = useLinearTags(allTagsForLinear);

  // Handle opening tags dialog for a group
  const handleOpenTagsDialog = useCallback((row: ErrorClusterGroupRow) => {
    setSelectedGroupForTags(row);
    setTagsDialogOpen(true);
  }, []);

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedGroupForTags(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected group workflows to dialog format with per-workflow tags
  const tagsDialogRows = useMemo(() => {
    if (!selectedGroupForTags) return [];
    return selectedGroupForTags.workflowsWithTags.map((workflow) => ({
      id: workflow.workflowId,
      tags: workflow.tags,
    }));
  }, [selectedGroupForTags]);

  // Fetch errors blocking cohorts
  const { data: blockingCohortData, loading: blockingCohortLoading } = useQuery(
    ERRORS_BLOCKING_COHORT_QUERY,
    {
      variables: {
        runId: selectedRunId ?? "",
      },
      skip: !selectedRunId,
    }
  );

  const handleRunChange = useCallback((runId: string) => {
    setSelectedRunId(runId);
  }, []);

  const [blockingCohortSortModel, setBlockingCohortSortModel] =
    useState<GridSortModel>([{ field: "cohortMembers", sort: "desc" }]);

  const blockingCohortColumns = useMemo(
    (): CustomGridColDef<ErrorBlockingCohortRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "activityType",
        headerName: "Activity Type",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.activityType)}
          </Typography>
        ),
      },
      {
        field: "cohortMembers",
        headerName: "Cohort Members",
        width: 150,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.cohortMembers}
          </Typography>
        ),
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorMessage}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorMessage}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={`/staff/cohorts/${row.cohortId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            endIcon={<OpenInNew />}
          >
            View Cohort
          </Button>
        ),
      },
    ],
    []
  );

  // Build the ticket title for display and submission
  const buildTicketInfo = useCallback(
    (row: ErrorClusterGroupRow) => {
      const omnisearch = encodeURIComponent(
        `${ERROR_CLUSTER_RUN_ID_KEY}:${selectedRunId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${row.groupId}`
      );
      const viewWorkflowsUrl = `${window.location.origin}/staff/fulfillment/workflows?omnisearch=${omnisearch}`;

      const sortedStates = [...row.states].sort();
      const statesDisplay =
        sortedStates.length <= 3 ? sortedStates.join(", ") : "Multiple States";
      const truncatedPattern =
        row.errorPattern.length > 25
          ? `${row.errorPattern.slice(0, 25)}...`
          : row.errorPattern;
      const title = `[${row.groupSize}: ${statesDisplay}] Fulfillment Error: ${truncatedPattern}`;
      const description = [
        `**Error Pattern**: ${row.errorPattern}`,
        "",
        `**View Workflows**: ${viewWorkflowsUrl}`,
        "",
        `**Group Size**: ${row.groupSize}`,
        "",
        `**States**: ${sortedStates.join(", ")}`,
      ].join("\n");

      return { title, description, sortedStates, viewWorkflowsUrl };
    },
    [selectedRunId]
  );

  // Open the Linear ticket confirmation dialog
  const handleCreateLinearTicket = useCallback((row: ErrorClusterGroupRow) => {
    setSelectedGroupForLinear(row);
    const { title } = buildTicketInfo(row);
    setLinearTicketTitle(title);
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
    setLinearConfirmDialogOpen(true);
  }, [buildTicketInfo]);

  // Close the Linear ticket confirmation dialog
  const handleCloseLinearConfirmDialog = useCallback(() => {
    setLinearConfirmDialogOpen(false);
    setSelectedGroupForLinear(null);
    setLinearTicketTitle("");
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
  }, []);

  // Actually create the Linear ticket after confirmation
  const confirmCreateLinearTicket = useCallback(async () => {
    if (!selectedGroupForLinear) return;

    const row = selectedGroupForLinear;
    const { description } = buildTicketInfo(row);

    setCreatingTicketForGroupId(row.groupId);

    try {
      // Create the Linear issue via API
      const result = await createLinearIssue({
        variables: {
          input: {
            title: linearTicketTitle,
            description,
            status: linearTicketStatus,
            priority: linearTicketPriority,
          },
        },
      });

      const issueIdentifier = result.data?.createLinearIssue?.issueIdentifier;
      const issueUrl = result.data?.createLinearIssue?.issueUrl;

      if (!issueIdentifier) {
        throw new Error("Failed to get issue identifier from Linear");
      }

      alerts.success(`Linear ticket ${issueIdentifier} created successfully!`);

      // Tag all workflows in the group with the ticket ID and "engineering" tag
      const workflowUpdates = row.workflowsWithTags.map((workflow) => ({
        workflowId: workflow.workflowId,
        tagNames: [
          ...new Set([...workflow.tags, issueIdentifier, "engineering"]),
        ],
      }));

      if (workflowUpdates.length > 0) {
        await updateWorkflowTags({
          variables: {
            workflowUpdates,
          },
        });

        alerts.success(
          `Tagged ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )} with ${issueIdentifier}`
        );
      }

      // Open the issue in a new tab
      if (issueUrl) {
        window.open(issueUrl, "_blank", "noopener");
      }

      // Close the dialog
      handleCloseLinearConfirmDialog();
    } catch (err) {
      alerts.error("Failed to create Linear ticket", err);
    } finally {
      setCreatingTicketForGroupId(null);
    }
  }, [
    selectedGroupForLinear,
    buildTicketInfo,
    createLinearIssue,
    updateWorkflowTags,
    alerts,
    handleCloseLinearConfirmDialog,
    linearTicketTitle,
    linearTicketStatus,
    linearTicketPriority,
  ]);

  const groupedColumns = useMemo(
    (): OmnisearchGridColDef<ErrorClusterGroupRow>[] => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: ({ row, id }) => <ExpandCollapseCell row={row} id={id} />,
      },
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "errorPattern",
        headerName: "Error Pattern",
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterKeyType: "text",
        renderCell: ({ row }) => (
          <Tooltip title={row.errorPattern}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorPattern}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "groupSize",
        headerName: "Group Size",
        width: 130,
        sortable: true,
        align: "right",
        headerAlign: "right",
        filterKeyType: "number",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.groupSize}
          </Typography>
        ),
      },
      {
        field: "workflowTags",
        headerName: "Tags",
        width: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.workflowTags.length === 0) return null;

          // Sort alphabetically and determine how many to show
          const sortedTags = [...row.workflowTags].sort();
          // Show max 3 tags, then overflow if more
          const maxVisibleTags = 3;
          const hasOverflow = sortedTags.length > maxVisibleTags;
          const visibleTags = hasOverflow
            ? sortedTags.slice(0, maxVisibleTags)
            : sortedTags;
          const overflowCount = sortedTags.length - maxVisibleTags;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTagsDialog(row);
              }}
            >
              {visibleTags.map((tag) => (
                <LinearTagChip
                  key={tag}
                  tag={tag}
                  linearStatusMap={linearStatusMap}
                  onNormalClick={() => handleOpenTagsDialog(row)}
                />
              ))}
              {hasOverflow && (
                <Chip
                  label={`+${overflowCount}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    minWidth: "auto",
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "untaggedWorkflowCount",
        headerName: "Untagged",
        width: 100,
        sortable: true,
        align: "right",
        headerAlign: "right",
        filterKeyType: "number",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.untaggedWorkflowCount}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const omnisearch = encodeURIComponent(
            `${ERROR_CLUSTER_RUN_ID_KEY}:${selectedRunId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${row.groupId}`
          );
          const isCreatingTicket = creatingTicketForGroupId === row.groupId;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                opacity: 0,
                transition: "opacity 0.3s ease",
                ".MuiDataGrid-row:hover &": {
                  opacity: 1,
                },
              }}
            >
              <Tooltip title="Manage Tags">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTagsDialog(row);
                  }}
                  aria-label="Manage Tags"
                >
                  <LocalOffer fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Workflows">
                <IconButton
                  size="small"
                  component={Link}
                  to={`/staff/fulfillment/workflows?omnisearch=${omnisearch}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  aria-label="View Workflows"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create Linear Ticket">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateLinearTicket(row);
                  }}
                  disabled={isCreatingTicket || createLinearLoading}
                  aria-label="Create Linear Ticket"
                >
                  {isCreatingTicket ? (
                    <CircularProgress size={16} />
                  ) : (
                    <LinearIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      selectedRunId,
      handleOpenTagsDialog,
      handleCreateLinearTicket,
      creatingTicketForGroupId,
      createLinearLoading,
      linearStatusMap,
    ]
  );

  // Detail panel callbacks
  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ErrorClusterGroupRow>) => {
      if (!selectedRunId || !row.workflowIds || row.workflowIds.length === 0) {
        return null;
      }
      return (
        <ErrorGroupDetailPanel runId={selectedRunId} groupId={row.groupId} />
      );
    },
    [selectedRunId]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<ErrorClusterGroupRow>) => {
      // Only expand if there are workflows to show
      if (params.row.workflowIds && params.row.workflowIds.length > 0) {
        apiRef.current?.toggleDetailPanel(params.id);
      }
    },
    [apiRef]
  );

  if (runsLoading) {
    return (
      <Stack py={3}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  // Handle case where there are no runs
  if (!runsData?.errorClusterRuns?.items?.length) {
    return (
      <Stack p={2}>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="h5" component="h5" color="text.primary" pb={1}>
            Error Triage Dashboard
          </Typography>
          <Typography fontSize={12} component="p" pb={3}>
            View and analyze errors from workflow activities
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Typography color="text.secondary">
              No error clustering runs found. Run an error group analysis to get
              started.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Tooltip title="Distance threshold controls cluster granularity. Lower values (0.5-1.0) create stricter clusters with higher semantic similarity. Higher values (1.0-2.0) create looser clusters. Default value is provided by the server.">
                <TextField
                  label="Distance Threshold"
                  type="number"
                  size="small"
                  value={distanceThresholdInput ?? ""}
                  onChange={(e) => setDistanceThresholdInput(e.target.value)}
                  disabled={configLoading || distanceThresholdInput === null}
                  inputProps={{
                    min: 0.5,
                    max: 2.0,
                    step: 0.1,
                  }}
                  sx={{ width: 160 }}
                />
              </Tooltip>
              <Tooltip title="Save distance threshold for future CRON jobs">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleSaveDistanceThreshold}
                    disabled={
                      savingConfig ||
                      !hasUnsavedChanges ||
                      distanceThresholdInput === null
                    }
                    sx={{ minWidth: 40, px: 1 }}
                  >
                    {savingConfig ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Save />
                    )}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleQueueJob}
                disabled={
                  queueJobLoading ||
                  configLoading ||
                  distanceThresholdInput === null
                }
              >
                {queueJobLoading ? "Queueing..." : "Run Error Group Analysis"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Stack>
    );
  }

  // Handle case where runs exist but no run is selected yet
  if (!selectedRunId) {
    return (
      <Stack py={3}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  const selectedRun = runsData?.errorClusterRuns?.items.find(
    (run) => run.runId === selectedRunId
  );

  return (
    <Stack p={2}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          pb: 3,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} pb={1}>
                <Typography variant="h5" component="h5" color="text.primary">
                  Error Triage Dashboard
                </Typography>
              </Box>
              <Typography fontSize={12} component="p">
                View and analyze errors from workflow activities
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <FormControl sx={{ flex: 1, maxWidth: 600 }}>
                <InputLabel id="run-select-label">Runs</InputLabel>
                <Select
                  labelId="run-select-label"
                  value={selectedRunId}
                  label="Clustering Run"
                  onChange={(e) => handleRunChange(e.target.value)}
                >
                  {runsData?.errorClusterRuns?.items.map((run) => (
                    <MenuItem key={run.runId} value={run.runId}>
                      <Box>
                        <Typography variant="body1">
                          {prettyPrintDateTimeMed(
                            new Date(run.updatedAt).toISOString()
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {run.groupCount} groups • {run.totalErrors} errors
                          {run.distanceThreshold != null &&
                            ` • dist: ${run.distanceThreshold}`}
                          {" "}• Run ID: {run.runId.slice(0, 8)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Tooltip title="Distance threshold controls cluster granularity. Lower values (0.5-1.0) create stricter clusters with higher semantic similarity. Higher values (1.0-2.0) create looser clusters. Default value is provided by the server.">
                    <TextField
                      label="Distance Threshold"
                      type="number"
                      size="small"
                      value={distanceThresholdInput ?? ""}
                      onChange={(e) => setDistanceThresholdInput(e.target.value)}
                      disabled={configLoading || distanceThresholdInput === null}
                      inputProps={{
                        min: 0.5,
                        max: 2.0,
                        step: 0.1,
                      }}
                      sx={{ width: 160 }}
                    />
                  </Tooltip>
                  <Tooltip title="Save distance threshold for future CRON jobs">
                    <span>
                      <Button
                        variant="outlined"
                        onClick={handleSaveDistanceThreshold}
                        disabled={
                          savingConfig ||
                          !hasUnsavedChanges ||
                          distanceThresholdInput === null
                        }
                        sx={{ minWidth: 40, px: 1 }}
                      >
                        {savingConfig ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Save />
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handleQueueJob}
                    disabled={
                      queueJobLoading ||
                      configLoading ||
                      distanceThresholdInput === null
                    }
                  >
                    {queueJobLoading ? "Queueing..." : "Run Error Group Analysis"}
                  </Button>
                </Box>
                {errorClusterGroupsData?.errorClusterGroupsByRunId?.items && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Untagged Groups:{" "}
                      {
                        errorClusterGroupsData.errorClusterGroupsByRunId.items.filter(
                          (group) => group.workflowTags.length === 0
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Untagged Workflows:{" "}
                      {errorClusterGroupsData.errorClusterGroupsByRunId.items.reduce(
                        (sum, group) => sum + (group.untaggedWorkflowCount ?? 0),
                        0
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {selectedRun && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Showing {selectedRun.groupCount} error groups with{" "}
                  {selectedRun.totalErrors} total errors
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, px: 3 }}>
            <Typography variant="h6">Error Groups</Typography>
            <Tooltip title="Workflow activity errors grouped by semantic similarity of the error message.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <OmnisearchDataGrid<
            ErrorClusterGroupsByRunIdQuery,
            ErrorClusterGroupsByRunIdQueryVariables,
            ErrorClusterGroupRow
          >
            persistenceKey="error-clustering-groups-table"
            isURLDriven={false}
            columns={groupedColumns}
            dataQuery={ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY}
            dataQueryVariables={{ runId: selectedRunId }}
            initialSortModel={[
              { field: "groupSize", sort: "desc" },
              { field: "groupId", sort: "asc" },
            ]}
            getItems={(data) => data.errorClusterGroupsByRunId?.items ?? []}
            getCount={(data) => data.errorClusterGroupsByRunId?.count ?? 0}
            getRowId={(row) => row.groupId}
            noDataMessage="No grouped error clusters found for this run."
            excludeFilterColumns={[
              "groupId",
              "facilitiesInGroupCount",
              "workflowTags",
              "actions",
            ]}
            additionalFilterColumns={[
              {
                key: "tags",
                header: "Tags",
                filterKeyType: "workflowTags",
              },
            ]}
            initialPageSize={25}
            rowHeight="auto"
            apiRef={apiRef}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            onRowClick={handleRowClick}
            omnisearchInputRef={omnisearchInputRef}
            onSearchChanged={(search) => {
              setCurrentSearch(search ?? "");
            }}
            commandButtons={({ search }) => {
              const handleToggleUntagged = () => {
                const searchToUse = search ?? currentSearch;
                const currentFilters = parseOmnisearch(searchToUse);
                const isCurrentlyActive =
                  currentFilters.untaggedWorkflowCount === ">0" ||
                  currentFilters.untaggedWorkflowCount === "> 0";

                if (isCurrentlyActive) {
                  // Remove untaggedWorkflowCount filter
                  const { untaggedWorkflowCount, ...restFilters } = currentFilters;
                  const newSearch = stringifyOmnisearch(restFilters);
                  // Update the omnisearch input directly
                  if (omnisearchInputRef.current) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                      window.HTMLInputElement.prototype,
                      "value"
                    )?.set;
                    nativeInputValueSetter?.call(omnisearchInputRef.current, newSearch);
                    const event = new Event("input", { bubbles: true });
                    omnisearchInputRef.current.dispatchEvent(event);
                  }
                } else {
                  // Add untaggedWorkflowCount:>0 filter
                  const newFilters = {
                    ...currentFilters,
                    untaggedWorkflowCount: ">0",
                  };
                  const newSearch = stringifyOmnisearch(newFilters);
                  if (omnisearchInputRef.current) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                      window.HTMLInputElement.prototype,
                      "value"
                    )?.set;
                    nativeInputValueSetter?.call(omnisearchInputRef.current, newSearch);
                    const event = new Event("input", { bubbles: true });
                    omnisearchInputRef.current.dispatchEvent(event);
                  }
                }
              };

              // Check if untagged filter is currently active
              const searchToUse = search ?? currentSearch;
              const currentFilters = parseOmnisearch(searchToUse);
              const isActive =
                currentFilters.untaggedWorkflowCount === ">0" ||
                currentFilters.untaggedWorkflowCount === "> 0";

              return [
                <IconButton
                  key="untagged-toggle"
                  onClick={handleToggleUntagged}
                  color="primary"
                  size="small"
                  title="Filter untagged groups"
                >
                  <Style />
                </IconButton>,
              ];
            }}
            gridSx={{
              "& .MuiDataGrid-detailPanel": {
                backgroundColor: "action.hover",
              },
              "& .MuiDataGrid-row.Mui-expanded": {
                backgroundColor: "action.hover",
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, px: 3 }}>
            <Typography variant="h6">Error Groups Blocking Cohorts</Typography>
            <Tooltip title="Error groups that are preventing entire cohorts from progressing. Shows the activity type, number of affected cohort members, and links to view the cohort details.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <Box sx={{ px: 3 }}>
            <DataGrid<ErrorBlockingCohortRow>
              columns={blockingCohortColumns}
              rows={blockingCohortData?.errorsBlockingCohort?.items ?? []}
              loading={blockingCohortLoading}
              getRowId={(row) => `${row.workflowId}-${row.groupId}`}
              noRowsMessage="No errors blocking cohorts found for this run."
              autoHeight
              pagination
              paginationMode="client"
              sortingMode="client"
              sortModel={blockingCohortSortModel}
              onSortModelChange={setBlockingCohortSortModel}
            />
          </Box>
        </Box>
      </Paper>

      {/* Manage Tags Dialog */}
      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="error-clustering-workflow-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />

      {/* Linear Ticket Confirmation Dialog */}
      <ConfirmDialog
        open={linearConfirmDialogOpen}
        onClose={handleCloseLinearConfirmDialog}
        onConfirm={confirmCreateLinearTicket}
        header="Create Linear Ticket"
        confirmText="Create Linear Ticket"
        cancelText="Cancel"
        loading={creatingTicketForGroupId !== null}
        confirmStartIcon={<LinearIcon />}
      >
        {selectedGroupForLinear && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              A Linear ticket will be created in the Engineering Support project.
            </Typography>

            <TextField
              label="Title"
              value={linearTicketTitle}
              onChange={(e) => setLinearTicketTitle(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                alignItems: "start",
                mb: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={linearTicketPriority}
                  onChange={(e) =>
                    setLinearTicketPriority(e.target.value as number)
                  }
                  label="Priority"
                >
                <MenuItem value={0}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        ---
                      </Typography>
                      <Typography>No priority</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      0
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ReportProblem fontSize="small" />
                      <Typography>Urgent</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      1
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SignalCellularAlt fontSize="small" />
                      <Typography>High</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      2
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ position: "relative", width: 24, height: 24 }}>
                        <SignalCellularAlt
                          fontSize="small"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.18,
                          }}
                        />
                        <SignalCellularAlt2Bar
                          fontSize="small"
                          sx={{ position: "absolute", inset: 0 }}
                        />
                      </Box>
                      <Typography>Medium</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      3
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={4}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ position: "relative", width: 24, height: 24 }}>
                        <SignalCellularAlt
                          fontSize="small"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.18,
                          }}
                        />
                        <SignalCellularAlt1Bar
                          fontSize="small"
                          sx={{ position: "absolute", inset: 0 }}
                        />
                      </Box>
                      <Typography>Low</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      4
                    </Typography>
                  </Box>
                </MenuItem>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <RadioGroup
                  value={linearTicketStatus}
                  onChange={(e) => setLinearTicketStatus(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="Triage"
                    control={<Radio />}
                    label="Triage"
                  />
                  <FormControlLabel
                    value="Todo"
                    control={<Radio />}
                    label="Todo"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Error Pattern
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedGroupForLinear.errorPattern}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" variant="body2">
                  Group Size: {selectedGroupForLinear.groupSize}
                </Typography>
                <Typography component="li" variant="body2">
                  States: {[...selectedGroupForLinear.states].sort().join(", ")}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary">
              After creation, all {selectedGroupForLinear.workflowIds.length}{" "}
              {pluralize("workflow", selectedGroupForLinear.workflowIds.length)}{" "}
              in this group will be tagged with the ticket ID and "engineering".
            </Typography>
          </Stack>
        )}
      </ConfirmDialog>
    </Stack>
  );
}
